import UI from "./ui";
import Utils from "./utils";
import { ASSETS_EXTENSIONS } from "../common/constants";

export default class Assets {
    constructor(apiKey) {
        this._apiKey = apiKey;
    }

    get apiKey() {
        return this._apiKey;
    }

    get permissions() {
        if (!this.apiKey) {
            return ["read-assets", "write-assets"];
        }
        const info = Utils.apiKeyToInfo(this.apiKey);
        return info.permissions || [];
    }

    get keyName() {
        if (!this.apiKey) {
            return null;
        }
        const info = Utils.apiKeyToInfo(this.apiKey);
        return info.keyName || "";
    }

    get isReadable() {
        return this.permissions.includes("read-assets");
    }

    get isWritable() {
        return this.permissions.includes("write-assets");
    }

    get isValidAPIKey() {
        if (!this.apiKey) {
            return true;
        }

        if (!Utils.isValidAPIKey(this.apiKey)) {
            return false;
        }
        return this.isReadable;
    }

    get isOwner() {
        // If the key is null, the user is accessing their own assets library
        return this.apiKey === null;
    }

    static parseKey(apiKey) {
        if (!apiKey) {
            return null;
        }
        const info = Utils.apiKeyToInfo(apiKey);
        return {
            id: Utils._apiKeyToHash(apiKey),
            name: info.keyName,
            permissions: info.permissions || [],
            key: apiKey,
        };
    }
    /**
     * Delete the asset at the given path.
     * @param {string} path The asset path to delete.
     * @returns {Promise<any>} Promise wrapping the API call.
     */
    static async deleteFile(path) {
        return this.MyAssetsLibrary.deleteFile(path);
    }

    async deleteFile(path) {
        return Utils.api("assets/delete", { path, recursive: false }, { apiKey: this._apiKey });
    }

    /**
     * Recursively delete assets at the given folder path.
     * @param {string} path The folder path to delete.
     * @returns {Promise<any>} Promise wrapping the API call.
     */
    static async deleteFolder(path) {
        return this.MyAssetsLibrary.deleteFolder(path);
    }

    async deleteFolder(path) {
        return Utils.api("assets/delete", { path, recursive: true }, { apiKey: this._apiKey });
    }
    /**
     * Delete the assets at the given paths, which do not need to be contiguous.
     * @param {string[]} paths Array of asset paths to delete.
     * @returns {Promise<any>} Promise wrapping the API call.
     */
    static async deleteMany(paths) {
        return this.MyAssetsLibrary.deleteMany(paths);
    }

    async deleteMany(paths) {
        return Utils.api("assets/delete", { paths, recursive: true }, { apiKey: this._apiKey });
    }

    /**
     * Rename one or more assets. This can also move them in the folder tree,
     * since an asset's name is its path.
     * @param {Object} mapping  Object defining which assets to rename. Each key is
     *                          the original asset name, the value is the new name.
     * @returns {Promise<any>} Promise wrapping the API call.
     */
    static async renameAssets(mapping) {
        return this.MyAssetsLibrary.renameAssets(mapping);
    }

    async renameAssets(mapping) {
        return Utils.api("assets/rename", { mapping }, { apiKey: this._apiKey });
    }

    /**
     * Create a new folder at the given path.
     * @param {string} path Full path for the new folder, ending with the folder's name.
     * @returns {Promise<any>} Promise wrapping the API call.
     */
    static async newFolder(path) {
        return this.MyAssetsLibrary.newFolder(path);
    }

    async newFolder(path) {
        return Utils.api("assets/new-folder", { path }, { apiKey: this._apiKey });
    }

    /**
     * This dual purpose function calls the relevant API for either creating Asset documents which reference existing S3 assets,
     * or uploading new data to S3 and creating corresponding Asset documents.
     * @param {FormData | {assets: {path: string, etag: string, size: number}[]}} data This arg can take the following forms,
     *      corresponding to its two functions:
     *      1) Create Asset documents in the database: An object `{ assets: {path: string, etag: string, size: number}[] }`.
     *      2) Upload binary data to S3: `FormData` with keys `paths[]` and `files[]`. `paths[]` contains the destination path
     *      (`string`) for each asset. `files[]` contains the binary data (`Blob`) and basename (`string`) for
     *      each asset. The indices of `paths[]` and `files[]` correspond to each other.
     * @param {boolean}  json           Send request as JSON.
     * @param {Function} [progress]     Progress callback with 3 arguments:
     *                                   `(stage: string, percent: number, progressData: Object)`.
     *                                   `progressData` is not manipulated by `uploadData`, but can be manipulated
     *                                   by the callback.
     * @param {Object}   progressData  Data object to pass to the progress callback as its 3rd arg.
     * @returns {Object}                API response.
     */
    static async uploadData(data, json, progress = null, progressData = null) {
        return this.MyAssetsLibrary.uploadData(data, json, progress, progressData);
    }

    async uploadData(data, json, progress = null, progressData = null) {
        let uploadProgress = null;
        if (progress) {
            uploadProgress = (step, percent) => {
                if (step === 0) {
                    progress("upload", 0, progressData);
                } else if (step === 1) {
                    progress("upload", percent, progressData);
                } else if (step === 3) {
                    progress("upload", 1, progressData);
                }
            };
        }
        const isDev = window.location.hostname.match(/(?:^|\.)(?:dev|qa|local|staging)\.forge-vtt\.com/);
        const url = isDev ? "assets/upload" : `https://upload.forge-vtt.com`;
        const response = await Utils.api(url, data, {
            progress: uploadProgress,
            json,
            apiKey: this._apiKey,
        });
        return response;
    }

    /**
     * Upload a single asset to the user's asset library.
     * @param {Blob} blob             Blob data for the asset file.
     * @param {string} filename       Asset's basename.
     * @param {string} destination    Folder path to upload the asset to.
     * @param {Function} [progress]   Progress callback with 3 arguments:
     *                                 (`stage: string`, `percent: number`, `progressData: Object`).
     *                                 `progressData` is not manipulated by `uploadData`, but can be manipulated
     *                                 by the callback.
     * @param {Object} progressData  Data object to pass to progress callback.
     * @returns {Object}              API response.
     */
    static async uploadAsset(blob, filename, destination, progress = null, progressData = null) {
        return this.MyAssetsLibrary.uploadAsset(blob, filename, destination, progress, progressData);
    }

    async uploadAsset(blob, filename, destination, progress = null, progressData = null) {
        const formData = new FormData();
        formData.append("path", destination);
        formData.append("file", blob, filename);
        return this.uploadData(formData, false, progress, progressData);
    }

    /**
     * Upload a set of assets to the user's assets library. Uses `this.uploadData` internally.
     * @param {{destination: string, blob: Blob, filename: string, etag: string, size: number}[]} assets
     *      Array of objects defining each asset to upload. `destination`: folder path to upload the asset to.
     *      `blob`: binary data to upload. `filename`: the asset's basename. `etag`: hash of the
     *      file data. `size`: file size in bytes.
     * @param {Function} [progress]     Progress callback
     * @param {Object}   progressData  Data object to pass to progress callback.
     * @returns {Object}                API response from `this.uploadData`.
     */
    static async uploadAssets(assets, progress = null, progressData = null) {
        return this.MyAssetsLibrary.uploadAssets(assets, progress, progressData);
    }

    async uploadAssets(assets, progress = null, progressData = null) {
        if (!assets || !assets.length) {
            return null;
        }
        const formData = new FormData();
        for (let asset of assets) {
            formData.append("paths[]", asset.destination);
            formData.append("files[]", asset.blob, asset.filename);
        }
        return this.uploadData(formData, false, progress, progressData);
    }

    /**
     * Creates an Asset document in the database which refers to an existing object in S3.
     * @param {{path: string, etag: string, size: number }[]} assets
     *      Array of asset objects to work on
     * @param {Function} [progress]     Progress callback, which is called whenever a create API is done. Arguments are:
     *      @param {Object}   response      Response for the current batch of assets getting created
     *      @param {Array}    batch         Current batch of assets being created
     *      @param {Number}   processed     Counter of the number of assets already processed (before the current batch)
     *      @param {Number}   total         Total number of assets to create
     *      If the callback returns false, abort the assets creation early.
     * @returns {{success: boolean, results: Object[]} | null}
     */
    static async createAssets(assets, progress = null) {
        return this.MyAssetsLibrary.createAssets(assets, progress);
    }

    async createAssets(assets, progress = null) {
        const CREATE_ASSETS_BATCH_SIZE = 250;
        const results = [];
        let error;
        for (let i = 0; i < assets.length; i += CREATE_ASSETS_BATCH_SIZE) {
            const batch = assets.slice(i, i + CREATE_ASSETS_BATCH_SIZE);
            const response = await Utils.api("assets/create", { assets: batch }, { apiKey: this._apiKey });
            if (response.success) {
                results.push(...response.results);
            } else {
                error = response.error;
            }
            if (progress) {
                const noCancel = await progress(response, batch, i, assets.length);
                if (noCancel === false) {
                    break;
                }
            }
        }
        return { success: !error, error, results };
    }

    /**
     * Ensure a set of assets exist by attempting to create Asset documents in the database. If this fails (because an object
     * with matching etag does not exist in S3), then upload the blob data to S3 and create an Asset from that.
     * @param {{destination: string, blob: Blob, filename: string, etag: string, size: number }[]} assets
     *      Array of asset objects to work on. Each object includes the metadata (destination path, filename, hash, and size),
     *      as well as a Blob with the binary data in case it needs to be uploaded.
     * @param {Function} [progress]     Progress callback, which is used with the following signatures for various sub-tasks:
     *      `(stage: "files", fractionDone: number, successes: number, failures: number): boolean`,
     *      `(stage: "hash", fractionDone: number, filename: string): boolean`, or
     *      `(stage: "upload", fractionDone: number, filename: string): boolean`.
     *      **Note**: the progress function returns a boolean, with false indicating the upload should be cancelled. Upstream
     *      functions may recognize and handle accordingly, but this function does not. Aborting between the `createAssets` call
     *      and the `uploadAssets` call would create a mess of side-effects, so the entire `ensureAssets` call will be completed
     *      before cancellation is checked next.
     * @param {Object} progressData    Data object to pass to progress callback.
     * @returns {{success: boolean, results: Object[]}}
     */
    static async ensureAssets(assets, progress = null, progressData = null) {
        return this.MyAssetsLibrary.ensureAssets(assets, progress, progressData);
    }

    async ensureAssets(assets, progress = null, progressData = null) {
        // Attempt to create all new assets with references to existing S3 objects.
        if (!assets || !assets.length) {
            return { success: true, results: [] };
        }
        const createRequest = { assets: [] };
        for (const asset of assets) {
            createRequest.assets.push({ path: asset.destination, etag: asset.etag, size: asset.size });
        }
        const response = await this.uploadData(createRequest, true, progress, progressData);
        if (!response.success) {
            return response;
        }
        const newAssets = [];
        // Find assets which do not match an existing S3 object. These assets will need to be
        // uploaded to S3.
        for (let idx = 0; idx < assets.length; idx++) {
            const asset = assets[idx];
            const result = response.results[idx];
            if (result.url === null) {
                newAssets.push(asset);
            }
        }
        // All assets already existed in S3, we're done.
        if (newAssets.length === 0) {
            return response;
        }
        // For any assets which did not already exist in S3, upload blob data to S3 and create
        // Asset documents which refer to the new S3 objects.
        const response2 = await this.uploadAssets(newAssets, progress, progressData);
        if (!response2.success) {
            return response2;
        }
        // Merge the results of both API calls to get accurate results for each asset.
        const mergedResults = [];
        for (let idx = 0, idx2 = 0; idx < response.results.length; idx++) {
            const result = response.results[idx];
            if (result.url === null) {
                mergedResults.push(response2.results[idx2]);
                idx2++;
            } else {
                mergedResults.push(result);
            }
        }
        return { success: true, results: mergedResults };
    }

    /**
     * Upload one or more files to a given path in the user's asset library.
     * @param {String} path          Base path prefix for all uploaded files.
     * @param {Blob[]} files         Array of `Blob`s with attributes {`name`, `assetPath` (defaults to name), `size`, `hash` (optional)}
     * @param {Function} [progress]  Progress callback, which is used with the following signatures for various sub-tasks:
     *      `(stage: "files", fractionDone: number, successes: number, failures: number): boolean`,
     *      `(stage: "hash", fractionDone: number, filename: string): boolean`, or
     *      `(stage: "upload", fractionDone: number, filename: string): boolean`.
     *      If the callback returns false, abort the upload early.
     * @param {{name: string, size: number, hash: string, url: string}[]} [remoteAssets] Remote assets to check against.
     */
    static async uploadFiles(path, files, progress = null, remoteAssets = null) {
        return this.MyAssetsLibrary.uploadFiles(path, files, progress, remoteAssets);
    }

    async uploadFiles(path, files, progress = null, remoteAssets = null) {
        if ((files || []).length === 0) {
            return { success: true, results: [] };
        }
        let assets = [];
        let size = 0;
        const results = [];
        let error = undefined;
        let successes = 0;
        // Local helper function to standardize the response when user cancels
        const cancelResult = () => {
            return { success: successes > 0, error: error || t("Upload cancelled"), results };
        };
        // Local helper function for performing the actual upload. Returns false if upload is cancelled.
        const doUpload = async () => {
            const displayName = assets.length === 1 ? assets[0].filename : `${assets.length} Files`;
            const response = await this.ensureAssets(assets, progress, displayName);
            if (response.success) {
                results.push(...response.results); // On multi upload, we get response for each in results
            } else {
                results.push(...[...Array(assets.length)].map(() => response)); // on error, set the error response to all the attempted uploads
            }
            successes = results.filter((r) => r.success).length;
            error = error || response.error;
            assets = [];
            size = 0;
            if (progress) {
                return progress("files", results.length / files.length, successes, results.length - successes);
            }
            return true;
        };
        if (progress) {
            progress("files", 0, 0, 0);
        }
        for (let file of files) {
            // Max 50 files or 50MB at a time for multi-file upload
            if (assets.length >= 50 || (assets.length > 0 && size + file.size > 50 * Assets.MB)) {
                if ((await doUpload()) === false) {
                    return cancelResult();
                }
            }
            const destination = Utils.cleanPath(`${path}/${file.assetPath || file.name}`);
            const hash =
                file.hash ||
                (await Utils.etagFromFile(file, (p) => {
                    if (progress) {
                        return progress("hash", p, destination);
                    }
                    return true;
                }));
            if (!hash) {
                return cancelResult();
            }

            if (remoteAssets) {
                const remoteFile = remoteAssets.find((a) => a.name === destination);
                if (remoteFile && remoteFile.size === file.size && remoteFile.hash === hash) {
                    results.push({ success: true, path: remoteFile.name, url: remoteFile.url });
                    successes += 1;
                    if (progress) {
                        if (
                            progress("files", results.length / files.length, successes, results.length - successes) ===
                            false
                        ) {
                            return cancelResult();
                        }
                    }
                    continue;
                }
            }
            assets.push({
                destination,
                blob: file,
                filename: file.name || Utils.basename(file.assetPath),
                etag: hash,
                size: file.size,
            });
            size += file.size;
        }
        // Upload final batch
        if (assets.length > 0) {
            // No point in "cancelling" based on the result of this call - it's the last one. The final batch will
            // be uploaded either way.
            await doUpload();
        }
        if (progress) {
            // Again, no point in returning the cancel result at this point. Full upload is finished anyway.
            progress("files", 1, successes, results.length - successes);
        }
        return { success: successes > 0, error, results };
    }

    static async uploadInput(input) {
        if (input.dataset.uploading) {
            return;
        }
        const form_group = $(input).closest(".form-group");
        const upload_progress = form_group.find(".upload-progress");
        const upload_button = form_group.find(".upload-button");
        if (upload_button) {
            upload_button
                .text("Uploading…")
                .prepend(`<span class="spinner-border spinner-border-sm mr-2 align-middle"> </span>`);
        }
        if (upload_progress) {
            upload_progress.empty();
        }
        input.dataset.uploading = true;
        const progress_bar = upload_progress ? UI.createProgressBar(upload_progress) : null;
        const response = await this.uploadFiles(input.dataset.path, input.files, (type, progress, filename) => {
            if (!upload_progress) {
                return;
            }
            progress = Math.round(progress * 100);
            if (type === "hash") {
                progress_bar.addClass("bg-secondary");
                progress_bar.setLabel(t("Processing {{- filename}}", { filename }));
            } else if (type === "upload") {
                progress_bar.addClass("bg-info");
                progress_bar.setLabel(t("Uploading {{- filename}}", { filename }));
            }
            progress_bar.bars.toggleClass("progress-bar-striped progress-bar-animated", progress !== 100);
            progress_bar.setProgress(progress);
        });
        const urls = response.results.map((r) => r.url).filter((u) => !!u);
        if (progress_bar) {
            const success =
                !response.error && (input.files.length === 1 ? !response.results[0].error : urls.length > 0);
            const error = response.error || response.results.map((r) => r.error).find((e) => !!e);
            const name =
                input.files.length === 1
                    ? input.files[0].name
                    : t(`{{count}} Files`, { count: success ? urls.length : input.files.length });
            progress_bar.setLabel(
                success
                    ? t(`{{- name}} uploaded successfully`, { name })
                    : // t("ASSETS.assets", {count})
                      t("ASSETS.UploadError", { assetsCount: input.files.length, error })
            );
            if (urls.length && urls.length < input.files.length) {
                progress_bar.bars.toggleClass("bg-warning", true);
                progress_bar.bars.toggleClass("bg-success", false);
                progress_bar.bars.toggleClass("bg-danger", false);
            } else {
                progress_bar.bars.toggleClass("bg-warning", false);
                progress_bar.bars.toggleClass("bg-success", success);
                progress_bar.bars.toggleClass("bg-danger", !success);
            }
        }
        if (upload_button) {
            upload_button.text(t("Upload"));
        }
        input.value = null;
        delete input.dataset.uploading;
        $(input).change();
        if (urls[0] && input.name.endsWith("-upload")) {
            const url_input_name = input.name.slice(0, -7);
            const url_input = form_group.find(`input[name="${url_input_name}"]`)[0];
            if (url_input) {
                url_input.value = urls[0];
                form_group.find(".note").removeClass("d-none");
            }
        }
        return urls;
    }

    static hookUploadForms() {
        this.hookUploadInputs($(".custom-file input[type=file]"), $(".upload-button"));
    }
    // cb: called if an upload happens
    static hookUploadInputs(inputs, upload_buttons, cb) {
        inputs.change((ev) => {
            const input = ev.currentTarget;
            const group = $(input).closest(".input-group");
            const isSet = input.files.length > 0;
            const filename = isSet ? input.files[0].name : "";
            group.find(".input-group-append").toggleClass("d-none", !isSet);
            group
                .find("label")
                .text(
                    input.files.length > 1
                        ? `${t("{{count}} Files", { count: input.files.length })}`
                        : isSet
                          ? filename
                          : t("Choose file")
                );
        });
        upload_buttons
            .click((ev) => {
                const input = $(ev.currentTarget).closest(".input-group").find("input")[0];
                this.uploadInput(input);
                if (cb) {
                    cb();
                }
            })
            .closest("form")
            .submit(async (ev) => {
                const form = $(ev.currentTarget);
                const toUpload = form.find(".custom-file input[type=file]");
                if (toUpload.val() != "") {
                    ev.preventDefault();
                    for (const input of toUpload) {
                        await this.uploadInput(input);
                    }
                    if (cb) {
                        cb();
                    }
                    form.submit();
                }
            });
    }

    /**
     * Get the full assets list for the user.
     * @returns {({isDirectory: false,
     *      basename: string,
     *      ctime: string,
     *      hash: string,
     *      mtime: string,
     *      name: string,
     *      size: number,
     *      url: string} |
     * {isDirectory: true,
     *      basename: string,
     *      ctime: string,
     *      mtime: string,
     *      name: string,
     *      size: number,
     *      children: Array})[]
     * } An array of asset and directory data. Each directory has an array of its children,
     * allowing the structure to fully recurse the assets tree.
     */
    static async getFiles() {
        return this.MyAssetsLibrary.getFiles();
    }

    async getFiles() {
        const library = await Utils.api("assets", null, {
            apiKey: this._apiKey,
        });
        // If updating files, the quotas need to also be updated
        this.quotas = {};
        return library.assets;
    }

    /**
     * Get the user's quota data.
     * @returns {{assets: {total: number, used: number},
     *            data: {total: number, used: number},
     *            upload: number}} The user's assets quota,
     *  data quota, and maximum upload size.
     */
    static async getQuotas() {
        Assets.quotas = await Utils.api("quotas");
        return Assets.quotas;
    }
    static async ensureQuotas() {
        if (this.quotas.assets === undefined) {
            return this.getQuotas();
        }
        return Assets.quotas;
    }

    /**
     * Parse the URL hash in an asset browser context into the search and path components.
     * @param {string} hash Hash string extracted from the URL.
     * @returns {{path: string, search: {terms: string[], scope: string}} | undefined} An object containing the
     * current path string, search terms, and search scope.
     */
    static parseHash(hash) {
        try {
            const props = Utils.hashProps(hash);
            return {
                path: props.path || "",
                search: props.search
                    ? {
                          terms: JSON.parse(props.search) || [],
                          scope: props.scope || "",
                      }
                    : undefined,
            };
        } catch (err) {
            return {
                path: "",
                search: undefined,
            };
        }
    }

    /**
     * Generate a URL hash for the current path and search of an asset browser context.
     * @param {string} path The current path string of the asset browser.
     * @param {{terms: string[], scope: string}} [search] An optional object containing the search terms and scope.
     */
    static genHash(path, search = undefined) {
        let hash = "";
        if (search) {
            if (!search.terms || !search.scope) {
                throw new Error("If search object is specified, it must include both terms and scope.");
            }
            if (!Assets.SEARCH_SCOPES.includes(search.scope)) {
                throw new Error(`Search scope must be one of the following: ${Assets.SEARCH_SCOPES}`);
            }
            hash = Utils.buildHash({ path, search: JSON.stringify(search.terms), scope: search.scope });
        } else if (path) {
            hash = Utils.buildHash({ path });
        }
        // Breadcrumbs will not change the url if the href is "#" so we need to set it to something for
        // an empty path so navigation continues to function
        if (hash === "") {
            hash = "path=";
        }
        // No search, return only the path component
        return `#${hash}`;
    }

    /**
     * Processes an array of assets in batches, downloading files and creating directories in a zip file.
     * Tracks overall progress across all batches and logs percentage completion.
     * @param {object[]} assets - Array of asset objects to process
     * @param {JSZip} zipFile - JSZip instance to add files to
     * @param {number} [batchSize] - Number of assets to process in each batch
     * @returns {Promise<object>} Object containing processing statistics
     * @throws {Error} If fetch fails for any file, logs error but continues processing
     */
    static async processAssetsInBatches(assets, zipFile, batchSize = 100) {
        let processed = 0;
        let prevPercentage = 0;
        const totalAssets = assets.length;

        const processAsset = async (asset) => {
            if (asset.isDirectory) {
                zipFile.folder(Utils.sanitizePath(asset.name));
            } else {
                try {
                    const response = await fetch(asset.url);
                    if (response.ok) {
                        zipFile.file(asset.name, await response.blob());
                    }
                } catch (error) {
                    console.error(error);
                }
            }

            processed++;
            const percentage = Math.round((processed * 100) / totalAssets);
            if (percentage > prevPercentage) {
                console.log(`Downloading assets: ${percentage}%`);
                prevPercentage = percentage;
            }
        };

        for (let i = 0; i < assets.length; i += batchSize) {
            const batch = assets.slice(i, i + batchSize);
            const batchPromises = batch.map((asset) => processAsset(asset));
            await Promise.all(batchPromises);
        }
    }

    /**
     * Download a list of assets as a zip file.
     * @param {Array<Asset>} assets     The list of assets to download.
     * @param Asset {url}            The URL of the asset to download.
     * @param Asset {name}           The name of the asset to download.
     * @param Asset {isDirectory}    Whether the asset is a directory.
     * @returns {Promise<void>}       A promise that resolves when the download is complete.
     * @throws {Error}               If the download fails.
     */
    static async downloadAssets(assets) {
        const zipFile = new JSZip();
        await this.processAssetsInBatches(assets, zipFile);

        let prevPercentage = 0;
        const blob = await zipFile.generateAsync(
            {
                type: "blob",
                compression: "DEFLATE",
                streamFiles: true,
                compressionOptions: { level: 1 },
            },
            (metadata) => {
                const percentage = Math.round(metadata.percent);
                if (percentage > prevPercentage) {
                    console.log(`Generated zip: ${percentage}%`);
                    prevPercentage = percentage;
                }
            }
        );
        await Utils.loadDependencies(["file-saver"]);
        saveAs(blob, "assets.zip");
    }

    /**
     * Recursively counts the total number of children for a given asset.
     * @param {object} asset - The asset object whose children are to be counted.
     * @returns {number} The total count of children, including nested children.
     */
    static childrenCount(asset) {
        let count = 0;
        if (!asset) {
            return count;
        }
        if (asset.children === undefined) {
            if (asset.isDirectory) {
                return count + 1;
            }
            return count;
        }
        count = asset.children.length;
        for (const child of asset.children) {
            count += Assets.childrenCount(child);
        }
        return count;
    }
}
Assets.quotas = {};
Assets.MyAssetsLibrary = new Assets();

Assets.IMAGE_EXTENSIONS = ASSETS_EXTENSIONS.IMAGE.map((ext) => ext.replace(".", ""));
Assets.VIDEO_EXTENSIONS = ASSETS_EXTENSIONS.VIDEO.map((ext) => ext.replace(".", ""));
Assets.AUDIO_EXTENSIONS = ASSETS_EXTENSIONS.AUDIO.map((ext) => ext.replace(".", ""));
Assets.FONT_EXTENSIONS = ASSETS_EXTENSIONS.FONT.map((ext) => ext.replace(".", ""));
Assets.ASSETS_3D_EXTENSIONS = ASSETS_EXTENSIONS.THREE_D.map((ext) => ext.replace(".", ""));
Assets.OTHER_MEDIA_EXTENSIONS = ASSETS_EXTENSIONS.OTHER_MEDIA.map((ext) => ext.replace(".", ""));
Assets.COMPRESSION_FILE_EXTENSIONS = ASSETS_EXTENSIONS.COMPRESSED.map((ext) => ext.replace(".", ""));
Assets.MEDIA_EXTENSIONS = [
    ...ASSETS_EXTENSIONS.MEDIA_EXTENSIONS.map((ext) => ext.replace(".", "")),
    ...Assets.COMPRESSION_FILE_EXTENSIONS,
];
Assets.INPUT_ACCEPT_TYPES = [
    "image/*",
    "video/*",
    "audio/*",
    "application/pdf",
    ...Assets.FONT_EXTENSIONS.map((ext) => `.${ext}`),
    ...Assets.ASSETS_3D_EXTENSIONS.map((ext) => `.${ext}`),
];
Assets.INPUT_ACCEPT_TYPE = Assets.INPUT_ACCEPT_TYPES.join(",");
Assets.SEARCH_SCOPES = ["local", "recursive", "global"];
Assets.MB = 1024 * 1024;
Assets.MAX_AUTOPLAY_SIZE = 5 * Assets.MB;
Assets.MAX_DOWNLOAD_SIZE = 500 * Assets.MB;
Assets.MAX_DOWNLOAD_FILES = 1000;
